import { useEffect, useMemo, useRef, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useBreadcrumbs } from "../context/BreadcrumbContext";
import { usePractice } from "../context/PracticeContext";
import { useConfirm } from "../context/ConfirmContext";
import {
  analyticsStudioApi,
  type SchemaContextMetric,
  type SchemaContextField,
  type StreamEvent,
} from "../api/analyticsStudio";
import { agentDashboardsApi } from "../api/agentDashboards";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { WidgetChart } from "../components/charts/WidgetChart";
import {
  Send,
  Sparkles,
  Loader2,
  Clock,
  AlertCircle,
  Save,
  LayoutDashboard,
  ChevronDown,
  ChevronRight,
  Search as SearchIcon,
  RefreshCw,
  MessageSquarePlus,
  Play,
  Trash2,
  Bookmark,
} from "lucide-react";
import { cn } from "../lib/utils";

const SAVED_QK = ["analytics-studio-saved"] as const;

interface RunState {
  conversationId: string | null;
  rationale: string;
  lookerQuery: SchemaContextMetric["query"] | null;
  viz: string;
  rows: Record<string, unknown>[];
  fields: { name: string; label: string; type: string }[];
  totalRows: number;
  queryTimeMs: number;
  analysis: string;
  followUps: string[];
  error: string | null;
  status: "idle" | "thinking" | "querying" | "analyzing" | "done" | "error";
  lastQuestion: string;
  turnCount: number;
}

const INITIAL: RunState = {
  conversationId: null,
  rationale: "",
  lookerQuery: null,
  viz: "bar",
  rows: [],
  fields: [],
  totalRows: 0,
  queryTimeMs: 0,
  analysis: "",
  followUps: [],
  error: null,
  status: "idle",
  lastQuestion: "",
  turnCount: 0,
};

const STARTERS = [
  "How many leads do we have by location, top 10?",
  "What's our review distribution by star rating?",
  "Which GMB profiles have the lowest call clicks?",
  "Show insurance mix as a percentage of total leads",
];

function statusLabel(s: RunState["status"]): string {
  switch (s) {
    case "thinking":
      return "Reading the schema and drafting a query…";
    case "querying":
      return "Running the query against Looker…";
    case "analyzing":
      return "Analysing the results…";
    case "done":
      return "Done";
    case "error":
      return "Error";
    default:
      return "Ask anything about your practice.";
  }
}

export function AnalyticsStudio({ simple = false }: { simple?: boolean } = {}) {
  const { setBreadcrumbs } = useBreadcrumbs();
  const { selectedPracticeId } = usePractice();
  const queryClient = useQueryClient();
  const confirm = useConfirm();
  const [question, setQuestion] = useState("");
  const [run, setRun] = useState<RunState>(INITIAL);
  const [schemaOpen, setSchemaOpen] = useState(true);
  const [schemaFilter, setSchemaFilter] = useState("");
  const [expandedMetricKey, setExpandedMetricKey] = useState<string | null>(null);
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  const [showDashboardPicker, setShowDashboardPicker] = useState(false);
  const [saveTitle, setSaveTitle] = useState("");
  const [savedExplorationId, setSavedExplorationId] = useState<string | null>(null);
  const [feedback, setFeedback] = useState<string | null>(null);
  const inFlight = useRef(false);

  useEffect(() => {
    setBreadcrumbs([{ label: "Analytics Studio" }]);
  }, [setBreadcrumbs]);

  // Load schema for the left-rail browser
  const { data: schema } = useQuery({
    queryKey: ["analytics-studio-schema", selectedPracticeId],
    queryFn: () => analyticsStudioApi.schema(selectedPracticeId!),
    enabled: !!selectedPracticeId,
    staleTime: 5 * 60 * 1000,
  });

  // Load dashboards for the promote-to-widget picker
  const { data: dashboards } = useQuery({
    queryKey: ["agent-dashboards", selectedPracticeId],
    queryFn: () => agentDashboardsApi.list(selectedPracticeId!),
    enabled: !!selectedPracticeId,
  });

  // Load saved explorations (the user's scratchpad)
  const { data: savedList } = useQuery({
    queryKey: [...SAVED_QK, selectedPracticeId],
    queryFn: () => analyticsStudioApi.listSaved(selectedPracticeId!),
    enabled: !!selectedPracticeId,
  });


  // Filter schema by search box
  const filtered = useMemo(() => {
    if (!schema) return { metrics: [], fields: [] as SchemaContextField[] };
    const q = schemaFilter.trim().toLowerCase();
    if (!q) return { metrics: schema.metrics, fields: schema.fields.slice(0, 80) };
    return {
      metrics: schema.metrics.filter(
        (m) =>
          m.label.toLowerCase().includes(q) ||
          m.key.toLowerCase().includes(q) ||
          m.description?.toLowerCase().includes(q),
      ),
      fields: schema.fields
        .filter(
          (f) =>
            f.name.toLowerCase().includes(q) ||
            f.snowflake_column.toLowerCase().includes(q) ||
            f.business_definition?.toLowerCase().includes(q),
        )
        .slice(0, 80),
    };
  }, [schema, schemaFilter]);

  // Save mutation
  const saveMutation = useMutation({
    mutationFn: () => {
      if (!selectedPracticeId || !run.lookerQuery) throw new Error("nothing to save");
      return analyticsStudioApi.save(selectedPracticeId, {
        title: saveTitle.trim() || run.lastQuestion.slice(0, 80),
        description: run.rationale,
        lookerQuery: run.lookerQuery,
        vizType: run.viz,
        config: {},
        lastQuestion: run.lastQuestion,
      });
    },
    onSuccess: (saved) => {
      setSavedExplorationId(saved.id);
      setShowSaveDialog(false);
      setFeedback(`Saved as "${saved.title}".`);
    },
    onError: (e) => setFeedback(`Save failed: ${e instanceof Error ? e.message : String(e)}`),
  });

  // Promote-to-widget mutation
  const promoteMutation = useMutation({
    mutationFn: async (dashboardId: string) => {
      let id = savedExplorationId;
      if (!id) {
        // Save first
        if (!selectedPracticeId || !run.lookerQuery) throw new Error("nothing to save");
        const saved = await analyticsStudioApi.save(selectedPracticeId, {
          title: saveTitle.trim() || run.lastQuestion.slice(0, 80),
          description: run.rationale,
          lookerQuery: run.lookerQuery,
          vizType: run.viz,
          lastQuestion: run.lastQuestion,
        });
        id = saved.id;
        setSavedExplorationId(id);
      }
      return analyticsStudioApi.promoteToWidget(selectedPracticeId!, id, dashboardId);
    },
    onSuccess: () => {
      setShowDashboardPicker(false);
      setFeedback("Added to dashboard as a widget.");
    },
    onError: (e) =>
      setFeedback(`Promote failed: ${e instanceof Error ? e.message : String(e)}`),
  });

  // Delete a saved exploration
  const deleteSavedMutation = useMutation({
    mutationFn: (id: string) =>
      analyticsStudioApi.delete(selectedPracticeId!, id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [...SAVED_QK, selectedPracticeId] });
      setFeedback("Saved exploration deleted.");
    },
  });

  /**
   * Run a metric's stored Looker query directly — no LLM query-generation
   * step. Used when an ops user just wants the "current value" of an
   * approved metric without typing.
   */
  async function runMetricDirectly(metric: SchemaContextMetric): Promise<void> {
    if (!selectedPracticeId) return;
    setFeedback(null);
    setSavedExplorationId(null);
    setRun({
      ...INITIAL,
      status: "querying",
      lastQuestion: `View metric: ${metric.label}`,
      rationale: `Direct run of approved metric '${metric.key}'.`,
      lookerQuery: metric.query,
      viz:
        metric.query.fields.length === 1
          ? "single_value"
          : metric.query.fields.length === 2
            ? "bar"
            : "table",
    });
    try {
      const res = await analyticsStudioApi.runDirect(selectedPracticeId, metric.query);
      setRun((prev) => ({
        ...prev,
        conversationId: res.conversationId,
        status: "done",
        rows: res.rows,
        fields: res.fields,
        totalRows: res.totalRows,
        queryTimeMs: res.queryTimeMs,
        viz: res.viz,
        analysis: res.analysis,
        followUps: res.followUpSuggestions,
        turnCount: 1,
      }));
    } catch (e) {
      setRun((prev) => ({
        ...prev,
        status: "error",
        error: e instanceof Error ? e.message : String(e),
      }));
    }
  }

  /**
   * Reload a saved exploration: runs its stored Looker query fresh, no LLM
   * query-generation. The user's original question stands in as context.
   */
  async function reloadSaved(saved: {
    id: string;
    title: string;
    lookerQuery: SchemaContextMetric["query"];
    vizType: string;
    lastQuestion: string | null;
    description: string | null;
  }): Promise<void> {
    if (!selectedPracticeId) return;
    setFeedback(null);
    setSavedExplorationId(saved.id);
    setRun({
      ...INITIAL,
      status: "querying",
      lastQuestion: saved.lastQuestion ?? saved.title,
      lookerQuery: saved.lookerQuery,
      viz: saved.vizType,
      rationale: saved.description ?? `Reloaded saved exploration: ${saved.title}`,
    });
    try {
      const res = await analyticsStudioApi.runDirect(selectedPracticeId, saved.lookerQuery);
      setRun((prev) => ({
        ...prev,
        conversationId: res.conversationId,
        status: "done",
        rows: res.rows,
        fields: res.fields,
        totalRows: res.totalRows,
        queryTimeMs: res.queryTimeMs,
        viz: saved.vizType, // honor saved viz choice
        analysis: res.analysis,
        followUps: res.followUpSuggestions,
        turnCount: 1,
      }));
    } catch (e) {
      setRun((prev) => ({
        ...prev,
        status: "error",
        error: e instanceof Error ? e.message : String(e),
      }));
    }
  }

  async function send(q: string, opts: { newConversation?: boolean } = {}) {
    if (!selectedPracticeId || !q.trim() || inFlight.current) return;
    inFlight.current = true;
    setFeedback(null);
    setSavedExplorationId(null);
    setRun((prev) => ({
      ...INITIAL,
      conversationId: opts.newConversation ? null : prev.conversationId,
      turnCount: opts.newConversation ? 0 : prev.turnCount,
      status: "thinking",
      lastQuestion: q,
    }));
    try {
      for await (const ev of analyticsStudioApi.nlqStream(
        selectedPracticeId,
        q,
        opts.newConversation ? undefined : run.conversationId ?? undefined,
      )) {
        applyEvent(ev);
      }
      setRun((prev) => ({ ...prev, turnCount: prev.turnCount + 1 }));
    } catch (err) {
      setRun((prev) => ({
        ...prev,
        status: "error",
        error: err instanceof Error ? err.message : String(err),
      }));
    } finally {
      inFlight.current = false;
    }
  }

  function applyEvent(ev: StreamEvent) {
    setRun((prev) => {
      switch (ev.event) {
        case "start":
          return { ...prev, conversationId: ev.data.conversationId };
        case "schema_loaded":
          return prev;
        case "query":
          return {
            ...prev,
            status: "querying",
            rationale: ev.data.rationale,
            lookerQuery: ev.data.lookerQuery,
            viz: ev.data.viz,
            followUps: ev.data.followUpSuggestions ?? [],
          };
        case "result":
          return {
            ...prev,
            status: "analyzing",
            rows: ev.data.rows,
            fields: ev.data.fields,
            totalRows: ev.data.totalRows,
            queryTimeMs: ev.data.queryTimeMs,
          };
        case "analysis_delta":
          return { ...prev, analysis: prev.analysis + ev.data.text };
        case "done":
          return { ...prev, status: "done" };
        case "error":
          return { ...prev, status: "error", error: ev.data.message };
        default:
          return prev;
      }
    });
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    void send(question);
  }

  function newConversation() {
    setRun(INITIAL);
    setQuestion("");
    setSavedExplorationId(null);
    setFeedback(null);
  }

  const isStreaming =
    run.status === "thinking" || run.status === "querying" || run.status === "analyzing";

  return (
    <div
      className={cn(
        simple ? "p-4 sm:p-6 max-w-3xl mx-auto" : "flex gap-4 p-6 max-w-[1400px] mx-auto",
      )}
    >
      {/* ── Schema browser (hidden in Simple mode) ───────────────── */}
      {!simple && (
      <aside
        className={cn(
          "shrink-0 transition-all space-y-3",
          schemaOpen ? "w-72" : "w-12",
        )}
      >
        <div className="sticky top-4 space-y-3">
        <div className="rounded-lg border border-border bg-card">
          <button
            className="flex w-full items-center gap-2 px-3 py-2.5 text-sm font-medium border-b border-border"
            onClick={() => setSchemaOpen((v) => !v)}
          >
            {schemaOpen ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
            {schemaOpen && (
              <>
                <span className="flex-1 text-left">Schema</span>
                <Badge variant="secondary" className="text-[10px]">
                  {schema?.metricCount ?? 0} · {schema?.fieldCount ?? 0}
                </Badge>
              </>
            )}
          </button>
          {schemaOpen && (
            <div className="p-2 space-y-2">
              <div className="flex items-center gap-2 px-2 py-1.5 rounded border border-input bg-background">
                <SearchIcon className="h-3 w-3 text-muted-foreground" />
                <input
                  type="text"
                  placeholder="Filter schema…"
                  className="flex-1 bg-transparent text-xs focus:outline-none"
                  value={schemaFilter}
                  onChange={(e) => setSchemaFilter(e.target.value)}
                />
              </div>
              <details open className="text-xs">
                <summary className="cursor-pointer px-2 py-1 font-medium text-muted-foreground uppercase tracking-wide text-[10px]">
                  Approved metrics ({filtered.metrics.length})
                </summary>
                <div className="space-y-0.5 mt-1 max-h-72 overflow-auto">
                  {filtered.metrics.map((m) => {
                    const isOpen = expandedMetricKey === m.key;
                    return (
                      <div key={m.key} className="rounded">
                        <button
                          className={cn(
                            "flex items-start gap-1 w-full text-left px-2 py-1 hover:bg-accent/40",
                            isOpen && "bg-accent/30",
                          )}
                          onClick={() =>
                            setExpandedMetricKey(isOpen ? null : m.key)
                          }
                        >
                          {isOpen ? (
                            <ChevronDown className="h-3 w-3 mt-0.5 shrink-0 text-muted-foreground" />
                          ) : (
                            <ChevronRight className="h-3 w-3 mt-0.5 shrink-0 text-muted-foreground" />
                          )}
                          <div className="flex flex-col min-w-0 flex-1">
                            <span className="font-medium truncate">{m.label}</span>
                            <span className="text-[10px] text-muted-foreground truncate">
                              {m.category}
                            </span>
                          </div>
                        </button>
                        {isOpen && (
                          <div className="px-2 pb-2 pt-1 space-y-1.5 border-l-2 border-accent ml-3 -mt-0.5">
                            {m.description && (
                              <p className="text-[11px] text-muted-foreground leading-snug">
                                {m.description}
                              </p>
                            )}
                            <div className="text-[10px] font-mono text-muted-foreground/80 break-all">
                              {m.query.explore} → {m.measureField.split(".").pop()}
                            </div>
                            <div className="flex gap-1 pt-1">
                              <Button
                                size="sm"
                                variant="outline"
                                className="h-6 text-[10px] px-2"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  void runMetricDirectly(m);
                                }}
                              >
                                <Play className="h-2.5 w-2.5 mr-1" />
                                Run
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                className="h-6 text-[10px] px-2"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setQuestion(
                                    `Explain the "${m.label}" metric and what's currently happening with it.`,
                                  );
                                }}
                              >
                                <Sparkles className="h-2.5 w-2.5 mr-1" />
                                Ask about this
                              </Button>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </details>
              <details className="text-xs">
                <summary className="cursor-pointer px-2 py-1 font-medium text-muted-foreground uppercase tracking-wide text-[10px]">
                  Raw fields ({filtered.fields.length}
                  {schema && filtered.fields.length === 80 ? "+" : ""})
                </summary>
                <div className="space-y-0.5 mt-1 max-h-72 overflow-auto">
                  {filtered.fields.map((f) => (
                    <button
                      key={`${f.snowflake_table}.${f.snowflake_column}`}
                      className="flex flex-col w-full text-left px-2 py-1 rounded hover:bg-accent/40"
                      onClick={() => setQuestion(f.name)}
                      title={f.business_definition}
                    >
                      <span className="font-mono text-[10px] truncate">
                        {f.snowflake_column}
                      </span>
                      <span className="text-[10px] text-muted-foreground truncate">
                        {f.category} · {f.unit_of_measure || "—"}
                      </span>
                    </button>
                  ))}
                </div>
              </details>
            </div>
          )}
        </div>

        {/* ── Saved explorations panel ────────────────────────── */}
        {schemaOpen && (
          <div className="rounded-lg border border-border bg-card">
            <div className="flex items-center gap-2 px-3 py-2.5 text-sm font-medium border-b border-border">
              <Bookmark className="h-3.5 w-3.5 text-muted-foreground" />
              <span className="flex-1">Saved</span>
              <Badge variant="secondary" className="text-[10px]">
                {savedList?.length ?? 0}
              </Badge>
            </div>
            <div className="p-2 space-y-1 max-h-72 overflow-auto text-xs">
              {(savedList ?? []).map((s) => (
                <div
                  key={s.id}
                  className={cn(
                    "rounded px-2 py-1.5 hover:bg-accent/30",
                    savedExplorationId === s.id && "bg-accent/40",
                  )}
                >
                  <div className="flex items-start gap-1">
                    <button
                      className="flex-1 min-w-0 text-left"
                      onClick={() => void reloadSaved(s)}
                      title={s.description ?? s.lastQuestion ?? s.title}
                    >
                      <div className="font-medium truncate">{s.title}</div>
                      {s.lastQuestion && (
                        <div className="text-[10px] text-muted-foreground truncate">
                          {s.lastQuestion}
                        </div>
                      )}
                    </button>
                    <button
                      className="shrink-0 text-muted-foreground hover:text-destructive"
                      onClick={async (e) => {
                        e.stopPropagation();
                        if (
                          await confirm({
                            title: `Delete "${s.title}"?`,
                            destructive: true,
                          })
                        ) {
                          deleteSavedMutation.mutate(s.id);
                        }
                      }}
                      title="Delete"
                    >
                      <Trash2 className="h-3 w-3" />
                    </button>
                  </div>
                </div>
              ))}
              {(!savedList || savedList.length === 0) && (
                <div className="text-center text-[11px] text-muted-foreground py-4">
                  No saved explorations yet. Save a result to come back to it.
                </div>
              )}
            </div>
          </div>
        )}
        </div>
      </aside>
      )}

      {/* ── Main column ───────────────────────────────────────── */}
      <div className="flex-1 min-w-0 space-y-4">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h2 className="text-xl font-bold">{simple ? "Ask anything" : "Analytics Studio"}</h2>
            <p className="text-sm text-muted-foreground mt-1">
              {simple
                ? "In plain English. Get a chart and a short read-out of what it means."
                : "Ask a question in plain English. The agent drafts a Looker query, runs it, charts the result, and tells you what to do about it."}
            </p>
          </div>
          {run.turnCount > 0 && (
            <Button size="sm" variant="ghost" onClick={newConversation}>
              <MessageSquarePlus className="h-3.5 w-3.5 mr-1.5" />
              New conversation
            </Button>
          )}
        </div>

        {/* Conversation status */}
        {run.turnCount > 0 && run.status === "done" && (
          <div className="text-xs text-muted-foreground flex items-center gap-2">
            <RefreshCw className="h-3 w-3" />
            Continuing conversation · {run.turnCount} turn{run.turnCount === 1 ? "" : "s"} so far
          </div>
        )}

        {/* Ask bar */}
        <form
          onSubmit={handleSubmit}
          className="flex gap-2 items-stretch rounded-lg border border-border bg-card p-2"
        >
          <Sparkles className="h-4 w-4 self-center text-muted-foreground ml-2" />
          <input
            type="text"
            className="flex-1 bg-transparent text-sm focus:outline-none px-1 py-2"
            placeholder={run.turnCount > 0 ? "Ask a follow-up… or paste a metric/field from the schema" : "e.g. how many leads by location last 30 days"}
            value={question}
            onChange={(e) => setQuestion(e.target.value)}
            disabled={isStreaming}
          />
          <Button
            type="submit"
            size="sm"
            disabled={!question.trim() || !selectedPracticeId || isStreaming}
          >
            {isStreaming ? (
              <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
            ) : (
              <Send className="h-3.5 w-3.5 mr-1.5" />
            )}
            Ask
          </Button>
        </form>

        {/* Starters */}
        {run.status === "idle" && (
          <div className="flex flex-wrap gap-2">
            {STARTERS.map((s) => (
              <button
                key={s}
                className="text-xs rounded-full border border-border bg-card hover:bg-accent/30 px-3 py-1.5 text-muted-foreground"
                onClick={() => {
                  setQuestion(s);
                  void send(s);
                }}
              >
                {s}
              </button>
            ))}
          </div>
        )}

        {/* Status line */}
        {run.status !== "idle" && (
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            {run.status === "error" ? (
              <AlertCircle className="h-3 w-3 text-destructive" />
            ) : run.status === "done" ? (
              <Clock className="h-3 w-3" />
            ) : (
              <Loader2 className="h-3 w-3 animate-spin" />
            )}
            {statusLabel(run.status)}
            {run.queryTimeMs > 0 && run.status === "done" && (
              <span className="ml-1">· {(run.queryTimeMs / 1000).toFixed(1)}s</span>
            )}
          </div>
        )}

        {/* Feedback */}
        {feedback && (
          <div className="rounded-md border border-border bg-muted/30 px-4 py-2 text-sm">
            {feedback}
          </div>
        )}

        {/* Error */}
        {run.error && (
          <div className="rounded-md border border-destructive/40 bg-destructive/5 px-4 py-2 text-sm text-destructive">
            {run.error}
          </div>
        )}

        {/* Rationale — inline in Pro mode, tucked behind a disclosure in Simple */}
        {run.rationale && !simple && (
          <div className="rounded-md border border-border bg-muted/30 px-4 py-2 text-xs space-y-1">
            <div className="text-muted-foreground">{run.rationale}</div>
            {run.lookerQuery && (
              <div className="font-mono text-[10px] text-muted-foreground/80">
                {run.lookerQuery.model}/{run.lookerQuery.explore} · fields:{" "}
                {run.lookerQuery.fields.map((f) => f.split(".").pop()).join(", ")}
                {run.lookerQuery.limit ? ` · limit ${run.lookerQuery.limit}` : ""}
              </div>
            )}
          </div>
        )}
        {run.rationale && simple && run.status === "done" && (
          <details className="rounded-md border border-border bg-muted/20 px-4 py-2 text-xs">
            <summary className="cursor-pointer text-muted-foreground select-none">
              Why this query?
            </summary>
            <div className="mt-2 space-y-1">
              <div className="text-muted-foreground">{run.rationale}</div>
              {run.lookerQuery && (
                <div className="font-mono text-[10px] text-muted-foreground/80">
                  {run.lookerQuery.model}/{run.lookerQuery.explore} · fields:{" "}
                  {run.lookerQuery.fields.map((f) => f.split(".").pop()).join(", ")}
                  {run.lookerQuery.limit ? ` · limit ${run.lookerQuery.limit}` : ""}
                </div>
              )}
            </div>
          </details>
        )}

        {/* Chart + action toolbar */}
        {run.rows.length > 0 && run.lookerQuery && (
          <div className="rounded-lg border border-border bg-card p-4">
            <div className="flex items-center justify-between mb-3 gap-2">
              <h3 className="text-sm font-medium flex-1 min-w-0 truncate">
                {run.lastQuestion}
              </h3>
              <Badge variant="secondary" className="text-[10px] uppercase">
                {run.viz}
              </Badge>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => {
                  setSaveTitle(run.lastQuestion.slice(0, 80));
                  setShowSaveDialog(true);
                }}
                disabled={run.status !== "done"}
              >
                <Save className="h-3.5 w-3.5 mr-1.5" />
                {simple
                  ? savedExplorationId
                    ? "Pinned"
                    : "Pin"
                  : savedExplorationId
                    ? "Saved"
                    : "Save"}
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => setShowDashboardPicker(true)}
                disabled={run.status !== "done"}
              >
                <LayoutDashboard className="h-3.5 w-3.5 mr-1.5" />
                Add to dashboard
              </Button>
            </div>
            <div style={{ height: 320 }}>
              <WidgetChart
                vizType={run.viz as "bar" | "line" | "area" | "pie" | "single_value" | "table"}
                data={{
                  fields: run.fields,
                  data: run.rows,
                  totalRows: run.totalRows,
                }}
                config={{}}
              />
            </div>
          </div>
        )}

        {/* Analysis */}
        {run.analysis && (
          <div className="rounded-lg border border-border bg-card p-4">
            <div className="flex items-center gap-2 mb-2">
              <Sparkles className="h-3.5 w-3.5 text-muted-foreground" />
              <h3 className="text-sm font-medium">Analysis</h3>
            </div>
            <div className="whitespace-pre-wrap text-sm leading-relaxed">
              {run.analysis}
            </div>
          </div>
        )}

        {/* Follow-ups */}
        {run.followUps.length > 0 && run.status === "done" && (
          <div className="space-y-2">
            <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
              {simple ? "Ask a follow-up" : "Follow-up questions (continues this conversation)"}
            </h3>
            <div className="flex flex-wrap gap-2">
              {run.followUps.map((s) => (
                <button
                  key={s}
                  className={cn(
                    "rounded-full border border-border bg-card hover:bg-accent/30",
                    simple ? "text-sm px-4 py-2" : "text-xs px-3 py-1.5",
                  )}
                  onClick={() => {
                    setQuestion(s);
                    void send(s);
                  }}
                >
                  {s}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* ── Save dialog ───────────────────────────────────────── */}
      {showSaveDialog && (
        <div
          className="fixed inset-0 z-50 bg-black/30 flex items-center justify-center"
          onClick={() => setShowSaveDialog(false)}
        >
          <div
            className="bg-card rounded-lg border border-border p-5 w-[420px] space-y-3"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-sm font-semibold">{simple ? "Pin this view" : "Save exploration"}</h3>
            <p className="text-xs text-muted-foreground">
              {simple
                ? "Pins to your personal list so you can come back to it. Use \"Add to dashboard\" to put it on a shared dashboard instead."
                : "Saves to your personal scratchpad. Use \"Add to dashboard\" if you want it on a shared dashboard instead."}
            </p>
            <input
              type="text"
              className="w-full rounded border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-[3px] focus:ring-ring"
              placeholder="Title"
              value={saveTitle}
              onChange={(e) => setSaveTitle(e.target.value)}
              autoFocus
            />
            <div className="flex justify-end gap-2 pt-1">
              <Button size="sm" variant="ghost" onClick={() => setShowSaveDialog(false)}>
                Cancel
              </Button>
              <Button
                size="sm"
                onClick={() => saveMutation.mutate()}
                disabled={saveMutation.isPending}
              >
                {saveMutation.isPending ? (simple ? "Pinning…" : "Saving…") : simple ? "Pin" : "Save"}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* ── Dashboard picker ──────────────────────────────────── */}
      {showDashboardPicker && (
        <div
          className="fixed inset-0 z-50 bg-black/30 flex items-center justify-center"
          onClick={() => setShowDashboardPicker(false)}
        >
          <div
            className="bg-card rounded-lg border border-border p-5 w-[520px] max-h-[80vh] overflow-auto space-y-3"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-sm font-semibold">Add to dashboard</h3>
            <p className="text-xs text-muted-foreground">
              Pick a dashboard. This exploration becomes a new widget on the
              next available row.
            </p>
            <div className="divide-y divide-border border border-border rounded">
              {(dashboards ?? []).map((d) => (
                <button
                  key={d.id}
                  className="flex flex-col w-full text-left px-3 py-2 hover:bg-accent/40 disabled:opacity-50"
                  onClick={() => promoteMutation.mutate(d.id)}
                  disabled={promoteMutation.isPending}
                >
                  <span className="text-sm font-medium">{d.title}</span>
                  {d.description && (
                    <span className="text-[11px] text-muted-foreground truncate">
                      {d.description}
                    </span>
                  )}
                </button>
              ))}
              {(!dashboards || dashboards.length === 0) && (
                <div className="px-3 py-4 text-xs text-muted-foreground text-center">
                  No dashboards yet. Visit /analytics first.
                </div>
              )}
            </div>
            <div className="flex justify-end gap-2 pt-1">
              <Button size="sm" variant="ghost" onClick={() => setShowDashboardPicker(false)}>
                Cancel
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
